import { useState, useEffect } from 'react';
import { updateUserPhone } from '@/services/settings';

interface PhoneChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPhone: string;
  userId: string;
  onSuccess: () => void;
}

export function PhoneChangeModal({ 
  isOpen, 
  onClose, 
  currentPhone, 
  userId,
  onSuccess 
}: PhoneChangeModalProps) {
  const [newPhone, setNewPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // 모달이 열릴 때마다 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setNewPhone('');
      setError('');
      setIsLoading(false);
    }
  }, [isOpen]);

  // 모달 외부 클릭 시 닫기 방지 (body overflow 제어)
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handlePhoneChange = (value: string) => {
    // 숫자만 입력 허용
    const numericValue = value.replace(/[^0-9]/g, '');
    
    // 11자리까지만 허용
    if (numericValue.length <= 11) {
      setNewPhone(numericValue);
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPhone.trim()) {
      setError('전화번호를 입력해주세요.');
      return;
    }

    if (newPhone === currentPhone) {
      setError('현재 전화번호와 동일합니다.');
      return;
    }

    if (newPhone.length < 10 || newPhone.length > 11) {
      setError('올바른 전화번호를 입력해주세요. (10-11자리)');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await updateUserPhone(userId, newPhone);
      
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.error || '전화번호 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('Phone update error:', error);
      setError('전화번호 변경 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div 
        className="relative bg-white rounded-xl w-full max-w-[340px] lg:max-w-[500px] shadow-xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className="text-center text-lg font-bold text-gray-900 mb-6">
            전화번호 변경
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 현재 전화번호 표시 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                현재 전화번호
              </label>
              <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600">
                {currentPhone || '등록되지 않음'}
              </div>
            </div>

            {/* 새 전화번호 입력 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                새 전화번호
              </label>
              <input
                type="tel"
                value={newPhone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="01012345678"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4CD964] focus:border-[#4CD964] ${
                  error ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
                maxLength={11}
              />
              <p className="text-xs text-gray-500 mt-1">
                '-' 없이 숫자만 입력해주세요
              </p>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                {error}
              </div>
            )}

            {/* 버튼들 */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                취소
              </button>
              
              <button
                type="submit"
                disabled={isLoading || !newPhone.trim()}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  !isLoading && newPhone.trim()
                    ? 'bg-[#4CD964] text-white hover:bg-[#45B85C]'
                    : 'bg-gray-300 text-gray-500'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    변경 중...
                  </div>
                ) : (
                  '변경하기'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}